import * as React from "react";
import { Textarea, type TextareaProps } from "./textarea";

export interface AutoTextareaProps extends Omit<TextareaProps, "rows"> {
  minRows?: number;
  maxRows?: number;
}

const AutoTextarea = React.forwardRef<HTMLTextAreaElement, AutoTextareaProps>(
  ({ minRows = 3, maxRows = 10, className, onChange, value, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const combinedRef = React.useCallback(
      (node: HTMLTextAreaElement) => {
        textareaRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";

      // Calculate the line height
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computedStyle.lineHeight, 10) || 20;

      // Calculate min and max heights
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;

      // Set the height based on content, respecting min/max constraints
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;

      // Show/hide scrollbar based on whether content exceeds maxHeight
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    }, [minRows, maxRows]);

    // Adjust height when value changes
    React.useEffect(() => {
      adjustHeight();
    }, [value, adjustHeight]);

    // Adjust height on mount
    React.useEffect(() => {
      adjustHeight();
    }, [adjustHeight]);

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(e);
        // Use setTimeout to ensure the value has been updated before adjusting height
        setTimeout(() => adjustHeight(), 0);
      },
      [onChange, adjustHeight],
    );

    return (
      <Textarea
        {...props}
        ref={combinedRef}
        value={value}
        onChange={handleChange}
        className={className}
        style={{
          resize: "none",
          overflow: "hidden",
          ...props.style,
        }}
      />
    );
  },
);

AutoTextarea.displayName = "AutoTextarea";

export { AutoTextarea };
